import { useQuery } from '@tanstack/react-query';
import { UsersThree, Scales, Warning } from '@phosphor-icons/react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchAllocationSuggestions } from '@/lib/workAllocationApi';
import { useAuthStore } from '@/features/auth/store';

const CLERK_ADMIN = new Set(['admin', 'senior_clerk', 'junior_clerk']);

/**
 * "Suggested counsel" — ranks chambers barristers for this matter by practice-area
 * match, availability and workload (Phase 4). Clerk/admin only; hides otherwise or
 * when there's nothing to suggest. The clerk still makes the actual offer.
 */
export function AllocationSuggestions({ matterId }: { matterId: string }) {
  const memberType = useAuthStore((s) => s.user?.memberType);
  const enabled = !!matterId && CLERK_ADMIN.has(memberType ?? '');
  const q = useQuery({
    queryKey: ['allocation-suggestions', matterId],
    queryFn: () => fetchAllocationSuggestions(matterId),
    enabled,
    retry: false,
  });

  if (!enabled || q.isError) return null;
  const data = q.data;
  if (!q.isLoading && (!data || data.candidates.length === 0)) return null;

  return (
    <Card>
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <UsersThree className="h-4 w-4 text-brand-600 dark:text-brand-400" /> Suggested counsel
          </div>
          {data?.practiceArea && <span className="text-xs text-muted-foreground">{data.practiceArea}</span>}
        </div>

        {data?.conflictStatus && data.conflictStatus !== 'clean' && (
          <div className="mb-3 flex items-center gap-1.5 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300">
            <Warning className="h-3.5 w-3.5" /> Conflict status: {data.conflictStatus} — check before allocating.
          </div>
        )}

        {q.isLoading ? (
          <div className="py-4 text-center text-xs text-muted-foreground">Ranking candidates…</div>
        ) : (
          <ol className="space-y-2">
            {data!.candidates.slice(0, 5).map((c, i) => (
              <li key={c.barristerId} className="flex items-start gap-3 rounded-md border border-border p-2.5">
                <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-medium text-muted-foreground">{i + 1}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <Scales className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="truncate text-sm font-medium text-foreground">{c.name}</span>
                    <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] uppercase text-muted-foreground">{c.memberType}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">{c.reasons.join(' · ')}</div>
                </div>
              </li>
            ))}
          </ol>
        )}
        <p className="mt-2 text-[11px] text-muted-foreground">A recommendation to help you brief — you still make the offer.</p>
      </CardContent>
    </Card>
  );
}
